import { SITE_URL, ORGANIZATION } from "./seoConfig";

// Mirrors the Schema.org FoodEstablishment subtypes that actually match the
// restaurant's real activityType, instead of labeling every restaurant a
// generic "Restaurant".
const ACTIVITY_TYPE_SCHEMA = {
  restaurant: "Restaurant",
  cafe: "CafeOrCoffeeShop",
  fastfood: "FastFoodRestaurant",
  bakery: "Bakery",
};

const SOCIAL_PLATFORM_HOSTS = {
  instagram: "instagram.com",
  telegram: "t.me",
  whatsapp: "wa.me",
  facebook: "facebook.com",
};

// Restaurant owners store social fields as either a full URL or a bare
// handle (same ambiguity already handled for Instagram in MenuIntro.js) —
// this generalizes that normalization to every social platform the schema
// actually has a field for.
export const normalizeSocialUrl = (platform, value) => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (platform === "website") return `https://${trimmed}`;
  const host = SOCIAL_PLATFORM_HOSTS[platform];
  if (!host) return null;
  const handle = trimmed.replace(/^@/, "");
  return `https://${host}/${handle}`;
};

export const resolveAssetUrl = (value, restaurantId) => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/^(https?:|data:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) return `${SITE_URL}${trimmed}`;
  return `${SITE_URL}/uploads/restaurants/${restaurantId}/${trimmed}`;
};

export const buildOrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: ORGANIZATION.name,
    alternateName: ORGANIZATION.alternateName,
    url: ORGANIZATION.url,
    logo: ORGANIZATION.logo,
    description: ORGANIZATION.description,
  };
  if (ORGANIZATION.sameAs?.length) schema.sameAs = ORGANIZATION.sameAs;
  return schema;
};

export const buildWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: ORGANIZATION.name,
  alternateName: ORGANIZATION.alternateName,
  url: SITE_URL,
});

// Builds a Schema.org Restaurant (or the closer-matching subtype) from a
// real restaurant document. Only fields backed by actual data are included —
// no invented ratings, reviews, opening hours, address, or price range.
export const buildRestaurantSchema = (restaurant) => {
  if (!restaurant || !restaurant._id || !restaurant.name) return null;

  const settings = restaurant.restaurantSettings || {};
  const restaurantId = restaurant._id;

  const schema = {
    "@context": "https://schema.org",
    "@type": ACTIVITY_TYPE_SCHEMA[restaurant.activityType] || "Restaurant",
    name: restaurant.name,
    url: `${SITE_URL}/restaurant/${restaurantId}`,
  };

  if (settings.description) schema.description = settings.description;
  if (restaurant.phone) schema.telephone = restaurant.phone;

  const images = [settings.profileImage, settings.coverImage]
    .map((value) => resolveAssetUrl(value, restaurantId))
    .filter(Boolean);
  if (images.length) schema.image = images.length === 1 ? images[0] : images;

  // showAddress is the restaurant owner's own privacy toggle for their
  // address — geo coordinates are just as location-identifying, so both
  // stay gated behind it.
  if (settings.showAddress !== false) {
    if (settings.address) schema.address = settings.address;

    const [longitude, latitude] = settings.location?.coordinates || [];
    const hasRealCoordinates = (latitude || longitude) && !(latitude === 0 && longitude === 0);
    if (hasRealCoordinates) {
      schema.geo = {
        "@type": "GeoCoordinates",
        latitude,
        longitude,
      };
    }
  }

  if (restaurant.menuLink) {
    schema.hasMenu = `${SITE_URL}/menu/${restaurant.menuLink}`;
  }

  const sameAs = Object.entries(settings.socialLinks || {})
    .map(([platform, value]) => normalizeSocialUrl(platform, value))
    .filter(Boolean);
  if (sameAs.length) schema.sameAs = sameAs;

  return schema;
};
